import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import CardTitle from "@saleor/components/CardTitle";
import ImageTile from "@saleor/components/ImageTile";
import ImageUpload from "@saleor/components/ImageUpload";
// import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import { ReorderAction } from "@saleor/types";
import createMultiFileUploadHandler from "@saleor/utils/handlers/multiFileUploadHandler";
import classNames from "classnames";
import React from "react";
import { useIntl } from "react-intl";
import { SortableContainer, SortableElement } from "react-sortable-hoc";

// import VariantS3ImageDialog from "../VariantS3ImageDialog";
// import {
//   useVariantS3ImageCreateMutation,
// } from "../../mutations";
import {
  ProductVariantDetails_productVariant,
  ProductVariantDetails_productVariant_images
} from "../../types/ProductVariantDetails";

const useStyles = makeStyles(
  theme => ({
    card: {
      marginTop: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        marginTop: 0
      }
    },
    fileField: {
      display: "none"
    },
    icon: {
      color: "rgba(255, 255, 255, 0.54)"
    },
    image: {
      height: "100%",
      objectFit: "contain",
      userSelect: "none",
      width: "100%"
    },
    imageContainer: {
      "&:hover, &.dragged": {
        "& $imageOverlay": {
          display: "block"
        }
      },
      background: "#ffffff",
      border: "1px solid #eaeaea",
      borderRadius: theme.spacing(),
      height: 140,
      margin: "auto",
      overflow: "hidden",
      padding: theme.spacing(2),
      position: "relative",
      width: 140
    },
    imageGridContainer: {
      position: "relative"
    },
    imageOverlay: {
      background: "rgba(0, 0, 0, 0.6)",
      cursor: "move",
      display: "none",
      height: 140,
      left: 0,
      padding: theme.spacing(2),
      position: "absolute",
      top: 0,
      width: 140
    },
    imageOverlayToolbar: {
      alignContent: "flex-end",
      display: "flex",
      position: "relative",
      right: -theme.spacing(3),
      top: -theme.spacing(2)
    },
    imageUpload: {
      height: "100%",
      left: 0,
      outline: 0,
      position: "absolute",
      top: 0,
      width: "100%"
    },
    imageUploadActive: {
      zIndex: 1
    },
    imageUploadIconActive: {
      display: "block"
    },
    root: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridRowGap: theme.spacing(2),
      gridTemplateColumns: "repeat(4, 1fr)",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(3, 1fr)"
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, 1fr)"
      }
    },
    rootDragActive: {
      opacity: 0.2
    }
  }),
  { name: "VariantImages" }
);

interface VariantImagesProps {
  placeholderImage?: string;
  images: ProductVariantDetails_productVariant_images[];
  variant: ProductVariantDetails_productVariant;
  loading?: boolean;
  onImageDelete: (id: string) => () => void;
  onImageEdit: (id: string) => () => void;
  onImageReorder?: ReorderAction;
  onImageFetch: (id: string) => () => void;
  onImageSetFirst: (id: string) => () => void;
  onClearImage: (id: string) => () => void;
  onVisibleEdit: (id: string) => void;
  onImageUpload(file: File);
}

interface SortableImageProps {
  image: {
    id: string;
    alt?: string;
    url: string;
    isFrist?: boolean;
    isVisible: boolean;
  };
  onImageEdit: (id: string) => void;
  onImageDelete: () => void;
  onImageSetFirst: () => void;
  onVisibleEdit: (id: string) => void;
}

const SortableImage = SortableElement<SortableImageProps>(
  ({ image, onImageEdit, onImageDelete, onImageSetFirst, onVisibleEdit }) => (
    <ImageTile
      image={image}
      onImageEdit={onImageEdit ? () => onImageEdit(image.id) : undefined}
      onImageDelete={onImageDelete}
      onImageSetFirst={onImageSetFirst}
      onVisibleEdit={onVisibleEdit}
      isVisible={!image?.isVisible}
    />
  )
);

interface ImageListContainerProps {
  className: string;
  items: ProductVariantDetails_productVariant_images[];
  preview: ProductVariantDetails_productVariant_images[];
  onImageDelete: (id: string) => () => void;
  onImageEdit: (id: string) => () => void;
  onImageSetFirst: (id: string) => () => void;
  onVisibleEdit: (id: string) => void;
}

const ImageListContainer = SortableContainer<ImageListContainerProps>(
  ({
    items,
    preview,
    onImageDelete,
    onImageEdit,
    onImageSetFirst,
    onVisibleEdit,
    ...props
  }) => (
    <div {...props}>
      {items.map((image, index) => (
        <SortableImage
          key={`item-${index}`}
          index={index}
          image={image}
          onImageEdit={onImageEdit ? onImageEdit(image.id) : null}
          onImageDelete={onImageDelete(image.id)}
          onImageSetFirst={onImageSetFirst(image.id)}
          onVisibleEdit={onVisibleEdit}
        />
      ))}
      {preview
        .sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1))
        .map(image => (
          <ImageTile
            loading={true}
            image={image}
            onVisibleEdit={onVisibleEdit}
          />
        ))}
    </div>
  )
);

const VariantImages: React.FC<VariantImagesProps> = props => {
  const {
    images,
    variant,
    placeholderImage,
    loading,
    onImageEdit,
    onImageDelete,
    onImageReorder,
    onImageFetch,
    onImageUpload,
    onImageSetFirst,
    onClearImage,
    onVisibleEdit
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();
  const upload = React.useRef(null);
  const [imagesToUpload, setImagesToUpload] = React.useState<
    ProductVariantDetails_productVariant_images[]
  >([]);

  const handleImageUpload = createMultiFileUploadHandler(onImageUpload, {
    onAfterUpload: () =>
      setImagesToUpload(prevImagesToUpload => prevImagesToUpload.slice(1)),
    onStart: files => {
      Array.from(files).forEach((file, fileIndex) => {
        const reader = new FileReader();
        reader.onload = event => {
          setImagesToUpload(prevImagesToUpload => [
            ...prevImagesToUpload,
            {
              __typename: "VariantImage",
              alt: "",
              id: "",
              isFirst: false,
              sortOrder: fileIndex,
              url: event.target.result as string,
              urlType: "image",
              isVisible: true
            }
          ]);
        };
        reader.readAsDataURL(file);
      });
    }
  });

  // const notify = useNotifier();
  // const [isModalOpened, setModalStatus] = React.useState(false);
  // const toggleModal = () => setModalStatus(!isModalOpened);
  // const [
  //   s3ImageCreate,
  //   s3ImageCreateOpts
  // ] = useVariantS3ImageCreateMutation({
  //   onCompleted: data => {

  //     toggleModal();
  //     const imageError = data.variantS3ImageCreate?.errors;
  //     if (imageError.length > 0) {
  //       notify({
  //         status: "error",
  //         text: imageError[0].code
  //       });
  //     }

  //   }
  // });

  return (
    <Card className={classes.card}>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Images",
          description: "section header"
        })}
        toolbar={
          <>
            <Button
              onClick={onClearImage(variant?.id)}
              disabled={loading}
              variant="text"
              color="primary"
              data-test="button-s3-image"
            >
              CLEAR ALL IMAGES
            </Button>
            <Button
              onClick={onImageFetch(variant?.id)}
              disabled={loading}
              variant="text"
              color="primary"
              data-test="button-s3-image"
            >
              S3 Image Fetch
            </Button>
            <Button
              onClick={() => upload.current.click()}
              disabled={loading}
              variant="text"
              color="primary"
              data-test="button-upload-image"
            >
              {intl.formatMessage(commonMessages.uploadImage)}
            </Button>
            <input
              className={classes.fileField}
              id="fileUpload"
              onChange={event => handleImageUpload(event.target.files)}
              multiple
              type="file"
              ref={upload}
              accept="image/*"
            />
          </>
        }
      />
      <div className={classes.imageGridContainer}>
        {images === undefined ? (
          <CardContent>
            <div className={classes.root}>
              <div className={classes.imageContainer}>
                <img className={classes.image} src={placeholderImage} />
              </div>
            </div>
          </CardContent>
        ) : images.length > 0 ? (
          <>
            <ImageUpload
              className={classes.imageUpload}
              isActiveClassName={classes.imageUploadActive}
              disableClick={true}
              hideUploadIcon={true}
              iconContainerActiveClassName={classes.imageUploadIconActive}
              onImageUpload={handleImageUpload}
            >
              {({ isDragActive }) => (
                <CardContent>
                  <ImageListContainer
                    distance={20}
                    helperClass="dragged"
                    axis="xy"
                    items={images}
                    preview={imagesToUpload}
                    onSortEnd={onImageReorder}
                    className={classNames({
                      [classes.root]: true,
                      [classes.rootDragActive]: isDragActive
                    })}
                    onImageDelete={onImageDelete}
                    onImageEdit={onImageEdit}
                    onImageSetFirst={onImageSetFirst}
                    onVisibleEdit={onVisibleEdit}
                  />
                </CardContent>
              )}
            </ImageUpload>
          </>
        ) : (
          <ImageUpload onImageUpload={handleImageUpload} />
        )}
      </div>

      {/* <VariantS3ImageDialog
        open={isModalOpened}
        confirmButtonState={s3ImageCreateOpts.status}
        disabled={s3ImageCreateOpts.loading}
        errors={s3ImageCreateOpts?.data?.variantS3ImageCreate.errors || []}
        onClose={toggleModal}
        onConfirm={formData =>
          s3ImageCreate({
            variables: {
              variant: variant.id,
              alt: variant.skuName,
              image: formData.image
            }
          })
        }
      />  */}
    </Card>
  );
};
VariantImages.displayName = "VariantImages";
export default VariantImages;
